// src/pages/financial-planning/tax-planning/DeductionsTable.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { loadTaxData } from "./taxData";
import { useTheme } from "../../../contexts/ThemeContext";
import { theme } from "../../../theme";

const TableContainer = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  border-radius: ${theme.radii.md};
  padding: 0.125rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[800] : theme.colors.neutral[50]};
`;

const Th = styled.th`
  padding: 0.375rem 0.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[300] : theme.colors.neutral[700]};
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
`;

const Tr = styled.tr`
  &:nth-child(even) {
    background-color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[800] : theme.colors.neutral[50]};
  }
  ${({ highlight, isDarkMode }) => highlight && `
    background-color: ${isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)'} !important;
    font-weight: ${theme.typography.fontWeights.semibold};
  `}
`;

const Td = styled.td`
  padding: 0.375rem 0.5rem;
  font-size: 0.75rem;
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[300] : theme.colors.neutral[700]};
  white-space: nowrap;
  ${({ highlight, isDarkMode }) => highlight && `
    background-color: ${isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.15)'};
    color: ${isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
    font-weight: ${theme.typography.fontWeights.semibold};
  `}
`;

const Disclaimer = styled.p`
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[400] : theme.colors.neutral[500]};
`;

export default function DeductionsTable({
  defaultYear,
  hideYearDropdown = false,
  contentOnly = false,
  highlightFilingStatus,
  highlightStandardDeduction,
  highlight65PlusDeduction,
  highlightBlindDeduction,
}) {
  const { isDarkMode } = useTheme();
  const currentYear = defaultYear || new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [deductions, setDeductions] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      const { DEDUCTIONS } = await loadTaxData();
      setDeductions(DEDUCTIONS);
      setAvailableYears(Object.keys(DEDUCTIONS).map(Number).sort((a, b) => b - a));
      setLoading(false);
    }
    fetch();
  }, []);

  useEffect(() => {
    if (availableYears.length && !deductions[selectedYear]) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear, deductions]);

  useEffect(() => {
    if (defaultYear && defaultYear !== selectedYear) {
      setSelectedYear(defaultYear);
    }
  }, [defaultYear]);

  if (loading || !availableYears.length || !deductions[selectedYear]) {
    return <div className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading deduction information…</div>;
  }

  const validYear = deductions[selectedYear] ? selectedYear : availableYears[0];

  const content = (
    <>
      <TableContainer>
        <Table>
          <Thead isDarkMode={isDarkMode}>
            <tr>
              <Th isDarkMode={isDarkMode}>Filing Status</Th>
              <Th isDarkMode={isDarkMode}>Standard</Th>
              <Th isDarkMode={isDarkMode}>65+ Deduction</Th>
              <Th isDarkMode={isDarkMode}>Blind Exemption</Th>
            </tr>
          </Thead>
          <tbody>
            {deductions[validYear].map((d, i) => {
              const isHighlightRow = d.status === highlightFilingStatus;
              return (
                <Tr key={i} isDarkMode={isDarkMode} highlight={isHighlightRow}>
                  <Td isDarkMode={isDarkMode}>{d.status}</Td>
                  <Td
                    isDarkMode={isDarkMode}
                    highlight={isHighlightRow && highlightStandardDeduction}
                  >
                    {d.standard.replace(" to ", "–")}
                  </Td>
                  <Td
                    isDarkMode={isDarkMode}
                    highlight={isHighlightRow && highlight65PlusDeduction}
                  >
                    {d.over65.replace(" to ", "–")}
                  </Td>
                  <Td
                    isDarkMode={isDarkMode}
                    highlight={isHighlightRow && highlightBlindDeduction}
                  >
                    {d.blind.replace(" to ", "–")}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
      <Disclaimer isDarkMode={isDarkMode}>
        * Additional deductions may apply. Please consult a tax professional for your specific situation.
      </Disclaimer>
    </>
  );

  return contentOnly ? content : content;
}
